import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import { 
  Users, Plus, Crown, Shield, User, Mail, Trash2, UserPlus, 
  LogOut, Check, X, ArrowLeft, Settings, UserCheck, Clock,
  FolderOpen, Play, FileCode, MessageSquare
} from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Teams() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [confirm, confirmDialog] = useConfirm();
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamDetails, setTeamDetails] = useState(null);
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  
  // Form state
  const [newTeamName, setNewTeamName] = useState("");
  const [newTeamDescription, setNewTeamDescription] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [transferUserId, setTransferUserId] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  
  // Notification config state
  const [notifConfig, setNotifConfig] = useState({
    slack_webhook_url: "",
    discord_webhook_url: "",
    notify_on_failure: true,
    notify_on_success: false,
  });
  const [notifSaving, setNotifSaving] = useState(false);

  useEffect(() => {
    fetchTeams();
    fetchInvites();
  }, []);

  useEffect(() => {
    if (selectedTeam) {
      fetchTeamDetails(selectedTeam);
    }
  }, [selectedTeam]);

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`, { withCredentials: true });
      setTeams(response.data.teams || []);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvites = async () => {
    try {
      const response = await axios.get(`${API}/teams/invites`, { withCredentials: true });
      setPendingInvites(response.data.invites || []);
    } catch (error) {
      console.error('Failed to fetch invites:', error);
    }
  };

  const fetchTeamDetails = async (teamId) => {
    try {
      const response = await axios.get(`${API}/teams/${teamId}`, { withCredentials: true });
      setTeamDetails(response.data);
      // Attempt to load notification config for owners/leaders
      if (["owner", "leader"].includes(response.data?.my_role)) {
        try {
          const n = await axios.get(`${API}/teams/${teamId}/notifications`, { withCredentials: true });
          setNotifConfig(n.data);
        } catch {
          // ignore - may be 403 for non-privileged
        }
      }
    } catch (error) {
      console.error('Failed to fetch team details:', error);
      toast.error('Failed to load team details');
    }
  };
  
  const saveNotifications = async () => {
    setNotifSaving(true);
    try {
      await axios.put(`${API}/teams/${selectedTeam}/notifications`, notifConfig, { withCredentials: true });
      toast.success('Notification settings saved');
    } catch (error) {
      console.error('Failed to save notifications:', error);
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setNotifSaving(false);
    }
  };
  
  const testWebhook = async (channel) => {
    try {
      await axios.post(`${API}/teams/${selectedTeam}/notifications/test`, { channel }, { withCredentials: true });
      toast.success(`Test ${channel} notification sent!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to send ${channel} test`);
    }
  };

  const createTeam = async () => {
    if (!newTeamName.trim()) {
      toast.error('Team name is required');
      return;
    }
    
    setActionLoading(true);
    try {
      await axios.post(`${API}/teams`, {
        name: newTeamName,
        description: newTeamDescription
      }, { withCredentials: true });
      
      toast.success('Team created!');
      setShowCreateModal(false);
      setNewTeamName("");
      setNewTeamDescription("");
      fetchTeams();
    } catch (error) {
      console.error('Failed to create team:', error);
      toast.error(error.response?.data?.detail || 'Failed to create team');
    } finally {
      setActionLoading(false);
    }
  };

  const inviteMember = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Email is required');
      return;
    }
    
    setActionLoading(true);
    try {
      const response = await axios.post(`${API}/teams/${selectedTeam}/invite`, {
        email: inviteEmail
      }, { withCredentials: true });
      
      if (response.data?.email_sent === false) {
        toast.warning(`Invite created for ${inviteEmail}, but email delivery failed. Verify the sender domain on Resend.`);
      } else {
        toast.success(`Invitation email sent to ${inviteEmail}`);
      }
      setShowInviteModal(false);
      setInviteEmail("");
      fetchTeamDetails(selectedTeam);
    } catch (error) {
      console.error('Failed to invite member:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setActionLoading(false);
    }
  };
  
  const resendInvite = async (inviteId) => {
    try {
      const res = await axios.post(`${API}/teams/invites/${inviteId}/resend`, {}, { withCredentials: true });
      if (res.data?.email_sent === false) {
        toast.warning('Email delivery failed. Check Resend domain verification.');
      } else {
        toast.success('Invitation email re-sent');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend');
    }
  };

  const acceptInvite = async (inviteId) => {
    try {
      await axios.post(`${API}/teams/invites/${inviteId}/accept`, {}, { withCredentials: true });
      toast.success('Joined team!');
      fetchTeams();
      fetchInvites();
    } catch (error) {
      console.error('Failed to accept invite:', error);
      toast.error('Failed to accept invite');
    }
  };

  const declineInvite = async (inviteId) => {
    try {
      await axios.post(`${API}/teams/invites/${inviteId}/decline`, {}, { withCredentials: true });
      toast.success('Invite declined');
      fetchInvites();
    } catch (error) {
      console.error('Failed to decline invite:', error);
      toast.error('Failed to decline invite');
    }
  };

  const setLeader = async (userId, memberName) => {
    const ok = await confirm({
      title: 'Assign as team leader?',
      description: `${memberName || 'This member'} will be promoted to Leader and gain permission to invite members, manage the team, and configure notifications.`,
      confirmLabel: 'Assign as Leader',
    });
    if (!ok) return;
    try {
      await axios.post(`${API}/teams/${selectedTeam}/set-leader`, {
        user_id: userId
      }, { withCredentials: true });
      toast.success('Leader assigned!');
      fetchTeamDetails(selectedTeam);
    } catch (error) {
      console.error('Failed to set leader:', error);
      toast.error(error.response?.data?.detail || 'Failed to set leader');
    }
  };

  const removeMember = async (memberId, memberName) => {
    const ok = await confirm({
      title: 'Remove member?',
      description: `${memberName || 'This member'} will be removed from the team and lose access to shared test cases.`,
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;

    try {
      await axios.delete(`${API}/teams/${selectedTeam}/members/${memberId}`, { withCredentials: true });
      toast.success('Member removed');
      fetchTeamDetails(selectedTeam);
    } catch (error) {
      console.error('Failed to remove member:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const leaveTeam = async () => {
    const ok = await confirm({
      title: 'Leave this team?',
      description: 'You will lose access to the team\'s shared test cases and execution history.',
      confirmLabel: 'Leave Team',
      destructive: true,
    });
    if (!ok) return;

    try {
      await axios.post(`${API}/teams/${selectedTeam}/leave`, {}, { withCredentials: true });
      toast.success('Left team');
      setSelectedTeam(null);
      setTeamDetails(null);
      fetchTeams();
    } catch (error) {
      console.error('Failed to leave team:', error);
      toast.error(error.response?.data?.detail || 'Failed to leave team');
    }
  };

  const transferOwnership = async () => {
    if (!transferUserId) {
      toast.error('Select a member');
      return;
    }
    
    const ok = await confirm({
      title: 'Transfer ownership?',
      description: 'You will become a regular member and the selected person will take full control of this team. This cannot be undone by you.',
      confirmLabel: 'Transfer Ownership',
      destructive: true,
    });
    if (!ok) return;

    setActionLoading(true);
    try {
      await axios.post(`${API}/teams/${selectedTeam}/transfer-ownership`, {
        user_id: transferUserId
      }, { withCredentials: true });
      toast.success('Ownership transferred!');
      setShowTransferModal(false);
      setTransferUserId("");
      fetchTeamDetails(selectedTeam);
    } catch (error) {
      console.error('Failed to transfer ownership:', error);
      toast.error(error.response?.data?.detail || 'Failed to transfer ownership');
    } finally {
      setActionLoading(false);
    }
  };

  const deleteTeam = async () => {
    const ok = await confirm({
      title: 'Delete this team permanently?',
      description: 'All team data, memberships, and sharing links will be removed. This cannot be undone.',
      confirmLabel: 'Delete Team',
      destructive: true,
    });
    if (!ok) return;

    try {
      await axios.delete(`${API}/teams/${selectedTeam}`, { withCredentials: true });
      toast.success('Team deleted');
      setSelectedTeam(null);
      setTeamDetails(null);
      fetchTeams();
    } catch (error) {
      console.error('Failed to delete team:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete team');
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'owner':
        return <Badge className="bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30"><Crown className="w-3 h-3 mr-1" />Owner</Badge>;
      case 'leader':
        return <Badge className="bg-[#00E599]/20 text-[#00E599] border-[#00E599]/30"><Shield className="w-3 h-3 mr-1" />Leader</Badge>;
      default:
        return <Badge className="bg-white/10 text-slate-300 border-white/20"><User className="w-3 h-3 mr-1" />Member</Badge>;
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
    {confirmDialog}
    <div className="min-h-screen bg-[#f8f9fb] text-slate-900" data-testid="teams-page">
      <div className="fixed inset-0 hero-glow" />
      
      <div className="relative z-10">
        <div className="container mx-auto px-4 py-8 max-w-6xl">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Button
              variant="ghost"
              className="text-slate-400 hover:text-white"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Team Management
            </h1>
          </div>

          {/* Pending Invites */}
          {pendingInvites.length > 0 && (
            <Card className="glass-card border-[#00E599]/30 rounded-xl mb-8">
              <CardHeader>
                <CardTitle className="flex items-center text-[#00E599]">
                  <Mail className="w-5 h-5 mr-2" />
                  Pending Invites ({pendingInvites.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pendingInvites.map((invite) => (
                    <div key={invite.invite_id} className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
                      <div>
                        <p className="font-medium">{invite.team_name}</p>
                        <p className="text-sm text-slate-400">Invited to join</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="bg-[#00E599] text-black hover:bg-[#00CC88]"
                          onClick={() => acceptInvite(invite.invite_id)}
                          data-testid={`accept-invite-btn-${invite.invite_id}`}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:text-red-300"
                          onClick={() => declineInvite(invite.invite_id)}
                          data-testid={`decline-invite-btn-${invite.invite_id}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Teams List */}
            <div className="lg:col-span-1">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Your Teams</h2>
                <Button
                  size="sm"
                  className="bg-[#D946EF] text-white hover:bg-[#C026D3]"
                  onClick={() => setShowCreateModal(true)}
                  data-testid="new-team-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  New Team
                </Button>
              </div>
              
              {loading ? (
                <p className="text-slate-400">Loading...</p>
              ) : teams.length === 0 ? (
                <Card className="glass-card border-white/5 rounded-xl">
                  <CardContent className="p-8 text-center">
                    <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">No teams yet</p>
                    <p className="text-sm text-slate-500 mt-2">Create a team to collaborate</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {teams.map((team) => (
                    <Card
                      key={team.team_id}
                      data-testid={`team-card-${team.team_id}`}
                      className={`glass-card rounded-xl cursor-pointer transition-all ${
                        selectedTeam === team.team_id 
                          ? 'border-[#00E599] bg-[#00E599]/5' 
                          : 'border-white/5 hover:border-white/20'
                      }`}
                      onClick={() => setSelectedTeam(team.team_id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#D946EF]/10 rounded-full flex items-center justify-center">
                            <Users className="w-5 h-5 text-[#D946EF]" />
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold">{team.name}</p>
                            {getRoleBadge(team.my_role)}
                          </div>
                          {team.is_primary && (
                            <Badge className="bg-[#00E599]/10 text-[#00E599] border-[#00E599]/20 text-xs">
                              Primary
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Team Details */}
            <div className="lg:col-span-2">
              {!selectedTeam ? (
                <Card className="glass-card border-white/5 rounded-xl h-full">
                  <CardContent className="p-12 text-center">
                    <Users className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">Select a team to view details</p>
                  </CardContent>
                </Card>
              ) : !teamDetails ? (
                <Card className="glass-card border-white/5 rounded-xl">
                  <CardContent className="p-8 text-center">
                    <p className="text-slate-400">Loading team details...</p>
                  </CardContent>
                </Card>
              ) : (
                <Card className="glass-card border-white/5 rounded-xl">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-2xl" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                          {teamDetails.team?.name}
                        </CardTitle>
                        <CardDescription className="mt-2">
                          {teamDetails.team?.description || 'No description'}
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        {getRoleBadge(teamDetails.my_role)}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Tabs defaultValue="members">
                      <TabsList className="bg-black/40 mb-6">
                        <TabsTrigger value="members" data-testid="members-tab">Members ({teamDetails.members?.length || 0})</TabsTrigger>
                        <TabsTrigger value="invites" data-testid="pending-invites-tab">Pending Invites</TabsTrigger>
                        <TabsTrigger value="notifications" data-testid="notifications-tab">Notifications</TabsTrigger>
                        <TabsTrigger value="settings" data-testid="settings-tab">Settings</TabsTrigger>
                      </TabsList>

                      <TabsContent value="members">
                        <div className="space-y-3">
                          {teamDetails.members?.map((member) => (
                            <div key={member.user_id} className="flex items-center justify-between p-4 bg-black/30 rounded-lg">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center overflow-hidden">
                                  {member.picture ? (
                                    <img src={member.picture} alt={member.name} className="w-full h-full object-cover" />
                                  ) : (
                                    <User className="w-5 h-5 text-slate-400" />
                                  )}
                                </div>
                                <div>
                                  <p className="font-medium">{member.name}</p>
                                  <p className="text-sm text-slate-400">{member.email}</p>
                                </div>
                                {getRoleBadge(member.team_role)}
                              </div>
                              
                              {/* Actions - only for owner/leader */}
                              {(teamDetails.my_role === 'owner' || teamDetails.my_role === 'leader') && 
                               member.user_id !== user?.user_id && 
                               member.team_role !== 'owner' && (
                                <div className="flex gap-2">
                                  {teamDetails.my_role === 'owner' && member.team_role !== 'leader' && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="text-[#00E599] hover:text-[#00CC88]"
                                          onClick={() => setLeader(member.user_id, member.name)}
                                          data-testid={`set-leader-btn-${member.user_id}`}
                                        >
                                          <Shield className="w-4 h-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Make Leader</TooltipContent>
                                    </Tooltip>
                                  )}
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-red-400 hover:text-red-300"
                                        onClick={() => removeMember(member.user_id, member.name)}
                                        data-testid={`remove-member-btn-${member.user_id}`}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Remove Member</TooltipContent>
                                  </Tooltip>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        
                        {/* Invite Button */}
                        {(teamDetails.my_role === 'owner' || teamDetails.my_role === 'leader') && (
                          <Button
                            className="mt-4 w-full bg-[#00E599] text-black hover:bg-[#00CC88]"
                            onClick={() => setShowInviteModal(true)}
                            data-testid="invite-member-btn"
                          >
                            <UserPlus className="w-4 h-4 mr-2" />
                            Invite Member
                          </Button>
                        )}
                      </TabsContent>

                      <TabsContent value="invites">
                        {teamDetails.pending_invites?.length === 0 ? (
                          <p className="text-slate-400 text-center py-8">No pending invites</p>
                        ) : (
                          <div className="space-y-3">
                            {teamDetails.pending_invites?.map((invite) => (
                              <div key={invite.invite_id} className="flex items-center justify-between p-4 bg-black/30 rounded-lg">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                                    <Mail className="w-5 h-5 text-slate-400" />
                                  </div>
                                  <div>
                                    <p className="font-medium">{invite.email}</p>
                                    <p className="text-sm text-slate-400 flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      Pending
                                    </p>
                                  </div>
                                </div>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-[#00E599] hover:text-[#00CC88]"
                                      onClick={() => resendInvite(invite.invite_id)}
                                      data-testid={`resend-invite-btn-${invite.invite_id}`}
                                    >
                                      <Mail className="w-4 h-4 mr-1" />
                                      Resend
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Re-send invitation email</TooltipContent>
                                </Tooltip>
                              </div>
                            ))}
                          </div>
                        )}
                      </TabsContent>

                      <TabsContent value="notifications">
                        {!["owner", "leader"].includes(teamDetails.my_role) ? (
                          <p className="text-slate-400 text-center py-8">
                            Only team owners or leaders can configure notifications
                          </p>
                        ) : (
                          <div className="space-y-5">
                            <div className="flex items-start gap-3 p-4 bg-black/30 border border-white/10 rounded-lg">
                              <MessageSquare className="w-5 h-5 text-[#D946EF] mt-1 flex-shrink-0" />
                              <div>
                                <p className="text-sm text-slate-200 font-medium">Team-level webhooks</p>
                                <p className="text-xs text-slate-500 mt-1">
                                  Configure once per team. All team test executions fire these webhooks based on the toggles below.
                                </p>
                              </div>
                            </div>
                            
                            {/* Slack webhook */}
                            <div>
                              <Label className="text-slate-300 mb-2 block">Slack Webhook URL</Label>
                              <div className="flex gap-2">
                                <Input
                                  type="url"
                                  placeholder="https://hooks.slack.com/services/..."
                                  className="bg-black/40 border-white/10 text-white"
                                  value={notifConfig.slack_webhook_url || ""}
                                  onChange={(e) => setNotifConfig({...notifConfig, slack_webhook_url: e.target.value})}
                                  data-testid="slack-webhook-input"
                                />
                                <Button
                                  variant="outline"
                                  className="border-white/10 text-slate-300 hover:text-white"
                                  onClick={() => testWebhook('slack')}
                                  disabled={!notifConfig.slack_webhook_url}
                                  data-testid="test-slack-btn"
                                >
                                  Test
                                </Button>
                              </div>
                              <p className="text-xs text-slate-500 mt-1">
                                Create in Slack: Apps → Incoming Webhooks → Add to Workspace
                              </p>
                            </div>
                            
                            {/* Discord webhook */}
                            <div>
                              <Label className="text-slate-300 mb-2 block">Discord Webhook URL</Label>
                              <div className="flex gap-2">
                                <Input
                                  type="url"
                                  placeholder="https://discord.com/api/webhooks/..."
                                  className="bg-black/40 border-white/10 text-white"
                                  value={notifConfig.discord_webhook_url || ""}
                                  onChange={(e) => setNotifConfig({...notifConfig, discord_webhook_url: e.target.value})}
                                  data-testid="discord-webhook-input"
                                />
                                <Button
                                  variant="outline"
                                  className="border-white/10 text-slate-300 hover:text-white"
                                  onClick={() => testWebhook('discord')}
                                  disabled={!notifConfig.discord_webhook_url}
                                  data-testid="test-discord-btn"
                                >
                                  Test
                                </Button>
                              </div>
                              <p className="text-xs text-slate-500 mt-1">
                                Create in Discord: Server Settings → Integrations → Webhooks → New Webhook
                              </p>
                            </div>
                            
                            {/* Event toggles */}
                            <div className="space-y-3 pt-2 border-t border-white/5">
                              <div className="flex items-center justify-between">
                                <div>
                                  <Label className="text-slate-200">Notify on failure</Label>
                                  <p className="text-xs text-slate-500 mt-0.5">Send a message when any test execution fails</p>
                                </div>
                                <Switch
                                  checked={!!notifConfig.notify_on_failure}
                                  onCheckedChange={(v) => setNotifConfig({...notifConfig, notify_on_failure: v})}
                                  data-testid="notify-on-failure-toggle"
                                />
                              </div>
                              <div className="flex items-center justify-between">
                                <div>
                                  <Label className="text-slate-200">Notify on success</Label>
                                  <p className="text-xs text-slate-500 mt-0.5">Send a message when any test execution passes (may be noisy)</p>
                                </div>
                                <Switch
                                  checked={!!notifConfig.notify_on_success}
                                  onCheckedChange={(v) => setNotifConfig({...notifConfig, notify_on_success: v})}
                                  data-testid="notify-on-success-toggle"
                                />
                              </div>
                            </div>
                            
                            <Button
                              className="w-full bg-[#00E599] text-black hover:bg-[#00CC88]"
                              onClick={saveNotifications}
                              disabled={notifSaving}
                              data-testid="save-notifications-btn"
                            >
                              {notifSaving ? 'Saving...' : 'Save Notification Settings'}
                            </Button>
                          </div>
                        )}
                      </TabsContent>

                      <TabsContent value="settings">
                        <div className="space-y-4">
                          {/* Leave Team */}
                          {teamDetails.my_role !== 'owner' && (
                            <Button
                              variant="outline"
                              className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
                              onClick={leaveTeam}
                              data-testid="leave-team-btn"
                            >
                              <LogOut className="w-4 h-4 mr-2" />
                              Leave Team
                            </Button>
                          )}
                          
                          {/* Owner-only actions */}
                          {teamDetails.my_role === 'owner' && (
                            <>
                              <Button
                                variant="outline"
                                className="w-full border-[#F59E0B]/30 text-[#F59E0B] hover:bg-[#F59E0B]/10"
                                onClick={() => setShowTransferModal(true)}
                                data-testid="transfer-ownership-btn"
                              >
                                <Crown className="w-4 h-4 mr-2" />
                                Transfer Ownership
                              </Button>
                              <Button
                                variant="outline"
                                className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
                                onClick={deleteTeam}
                                data-testid="delete-team-btn"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete Team
                              </Button>
                            </>
                          )}
                          
                          {/* View Shared Dashboard */}
                          <Button
                            className="w-full bg-[#D946EF] text-white hover:bg-[#C026D3]"
                            onClick={() => navigate(`/teams/${selectedTeam}/shared`)}
                            data-testid="view-shared-dashboard-btn"
                          >
                            <FolderOpen className="w-4 h-4 mr-2" />
                            View Shared Dashboard
                          </Button>
                        </div>
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Team Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-[#0B0F1A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Unbounded, sans-serif' }}>Create New Team</DialogTitle>
            <DialogDescription className="text-slate-400">
              Create a team to collaborate with others
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Team Name *</Label>
              <Input
                placeholder="e.g., QA Team, Project Alpha"
                className="mt-2 bg-black/40 border-white/10 text-white"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                data-testid="new-team-name-input"
              />
            </div>
            <div>
              <Label className="text-slate-300">Description</Label>
              <Textarea
                placeholder="What is this team for?"
                className="mt-2 bg-black/40 border-white/10 text-white"
                value={newTeamDescription}
                onChange={(e) => setNewTeamDescription(e.target.value)}
                data-testid="new-team-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button 
              className="bg-[#00E599] text-black hover:bg-[#00CC88]"
              onClick={createTeam}
              disabled={actionLoading}
              data-testid="create-team-confirm-btn"
            >
              {actionLoading ? 'Creating...' : 'Create Team'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Member Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="bg-[#0B0F1A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Unbounded, sans-serif' }}>Invite Member</DialogTitle>
            <DialogDescription className="text-slate-400">
              Send an invite to join this team
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-slate-300">Email Address *</Label>
            <Input
              type="email"
              placeholder="colleague@example.com"
              className="mt-2 bg-black/40 border-white/10 text-white"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              data-testid="invite-email-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowInviteModal(false)}>Cancel</Button>
            <Button 
              className="bg-[#00E599] text-black hover:bg-[#00CC88]"
              onClick={inviteMember}
              disabled={actionLoading}
              data-testid="send-invite-btn"
            >
              {actionLoading ? 'Sending...' : 'Send Invite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Ownership Modal */}
      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="bg-[#0B0F1A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Unbounded, sans-serif' }}>Transfer Ownership</DialogTitle>
            <DialogDescription className="text-slate-400">
              Select a member to become the new owner. You will become a regular member.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-slate-300 mb-3 block">Select New Owner</Label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {teamDetails?.members?.filter(m => m.user_id !== user?.user_id).map((member) => (
                <div
                  key={member.user_id}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    transferUserId === member.user_id 
                      ? 'bg-[#00E599]/20 border border-[#00E599]/50' 
                      : 'bg-black/30 hover:bg-black/50'
                  }`}
                  onClick={() => setTransferUserId(member.user_id)}
                >
                  <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-slate-400" />
                  </div>
                  <div>
                    <p className="font-medium">{member.name}</p>
                    <p className="text-xs text-slate-400">{member.email}</p>
                  </div>
                  {transferUserId === member.user_id && (
                    <Check className="w-5 h-5 text-[#00E599] ml-auto" />
                  )}
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowTransferModal(false)}>Cancel</Button>
            <Button 
              className="bg-[#F59E0B] text-black hover:bg-[#D97706]"
              onClick={transferOwnership}
              disabled={actionLoading || !transferUserId}
            >
              {actionLoading ? 'Transferring...' : 'Transfer Ownership'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
}
